<template>
  <div class="tutorials-page" style="position: relative">
    <SingleRideHeader :presentedText="t('tutorialsPageTitle')" />
    <b-container>
      <div class="tutorials-page-section after_page_header">
        <div class="container">
          <div class="row">
            <div class="col-12">
              <div class="tutorials-page-content">
                <h1>{{ t('tutorialsPageTitle') }}</h1>
                <p class="intro-text">{{ t('tutorialsPageDescription') }}</p>
                
                <div class="questions-list">
                  <div 
                    v-for="(tutorial, index) in tutorials" 
                    :key="index"
                    class="question-item"
                  >
                    <div 
                      class="question-card"
                      :class="{ 'is-open': selectedTutorialIndex === index }"
                      @click="toggleTutorial(index)"
                    >
                      <div class="question-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 19H11V17H13V19ZM15.07 11.25L14.17 12.17C13.45 12.9 13 13.5 13 15H11V14.5C11 13.67 11.45 12.9 12.17 12.17L13.1 11.25C13.45 10.9 13.67 10.47 13.67 10C13.67 9.03 12.9 8.25 11.93 8.25C10.96 8.25 10.18 9.03 10.18 10H8.25C8.25 7.76 10.09 5.92 12.33 5.92C14.57 5.92 16.42 7.76 16.42 10C16.42 10.88 16.1 11.68 15.07 12.71V11.25Z" fill="#0d2f29"/>
                        </svg>
                      </div>
                      <div class="question-text">
                        <h3>{{ tutorial.title }}</h3>
                      </div>
                      <div class="question-arrow" :class="{ 'is-open': selectedTutorialIndex === index }">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M7.5 15L12.5 10L7.5 5" stroke="#0d2f29" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </div>
                    </div>
                    
                    <transition name="slide-down">
                      <div v-if="selectedTutorialIndex === index" class="video-container-wrapper">
                        <div class="tutorial-video-container">
                          <video 
                            v-if="tutorial.videoUrl && !tutorial.error"
                            :ref="`video-${index}`"
                            :src="tutorial.videoUrl"
                            controls
                            autoplay
                            class="tutorial-video"
                            @loadstart="onVideoLoadStart(index)"
                            @error="onVideoError(index)"
                            @loadeddata="onVideoLoaded(index)"
                            preload="none"
                          >
                            {{ t('videoNotSupported') }}
                          </video>
                          <div v-else-if="tutorial.loading" class="video-loading">
                            <div class="spinner-border text-primary" role="status">
                              <span class="sr-only">{{ t('loading') }}</span>
                            </div>
                            <p>{{ t('loadingVideo') }}</p>
                          </div>
                          <div v-else-if="tutorial.error" class="video-error">
                            <p>{{ t('videoLoadError') }}</p>
                            <button class="retry-button" @click="retryVideo(index)">{{ t('retry') }}</button>
                          </div>
                        </div>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </b-container>
    <PageFooter />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import PageFooter from "@/components/PageFooter.vue";
import SingleRideHeader from "@/components/SingleRideHeader";
import { translationMixin } from '@/mixins/translationMixin';

export default {
  name: "TutorialsPage",
  mixins: [translationMixin],
  components: {
    PageFooter,
    SingleRideHeader,    
  },
  data() {
    return {
      tutorialState: [
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false },
        { loading: false, error: false }
      ],
      selectedTutorialIndex: null
    };
  },
  computed: {
    ...mapGetters(['globalRides', 'userId']),
    tutorials() {
      const defs = [
        { titleKey: 'tutorialsCreateNewItinerary', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fadding_new_ride_5.mp4?alt=media&token=f2883501-d048-4468-9c99-f8cf671ee456' },
        { titleKey: 'tutorialsAddActivity', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fadding_new_atraction_base_5.mp4?alt=media&token=2aaaf1d8-4cfa-42f9-89b3-3010f7d3d86c' },
        { titleKey: 'tutorialsAddActivityWithLocation', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fadding_new_activity_with_location5.mp4?alt=media&token=cab553a2-26c6-4912-a2b5-42532391d090' },
        { titleKey: 'tutorialsAddAccommodation', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fadding_accommodation_5.mp4?alt=media&token=4fe5d3b4-e433-4ecb-9431-abb1a8b8322d' },
        { titleKey: 'tutorialsInsertLinksInText', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fadding_link_to_text5.mp4?alt=media&token=f78cc469-2b5e-4898-a871-8a118955e4a8' },
        { titleKey: 'tutorialsShareRide', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fshering_the_Ride_5.mp4?alt=media&token=fe56a71b-d9bb-4116-91ca-8997ceb67581' },
        { titleKey: 'tutorialsAddDayInfo', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fadding_more_informatuin_for_day_5.mp4?alt=media&token=e23b4988-eb73-4249-904f-31cd481ffaff' },
        { titleKey: 'tutorialsReorderActivitiesInDay', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Freordering_the_atraction_5.mp4?alt=media&token=8f123014-8711-4cd2-9398-756de0ab9953' },
        { titleKey: 'tutorialsAddTripGeneralInfo', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fadding_more_information_5.mp4?alt=media&token=9a0f0c64-3ce6-4ebf-836c-5ce7edf4f74d' },
        { titleKey: 'tutorialsCopyItineraryForClient', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fcopy_and_adapt_the_trip_5.mp4?alt=media&token=13b06643-2f47-4063-b49d-41d098a95034' },
        { titleKey: 'tutorialsAddFromMyActivities', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fadd_atraction_from_Pool_5.mp4?alt=media&token=4228b49f-b64b-461f-9c2f-11378d6ab0cd' },
        { titleKey: 'tutorialsEditDayInCalendar', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fediting_ride_from_calender_5.mp4?alt=media&token=010d1170-5eb0-440d-9604-06d7d9e15284' },
        { titleKey: 'tutorialsManageItinerariesFolders', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Ffiling_rides_5.mp4?alt=media&token=58dbb24b-da8c-4190-b5a3-310f6ab5fc11' },
        { titleKey: 'tutorialsEditFromMyActivitiesRepo', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2Fediting_the_pool_activity_5.mp4?alt=media&token=30b3160a-8154-4b20-b2a7-e1c3600b2a59' },
        { titleKey: 'tutorialsGeneralExplanation', videoUrl: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/tutorials%2FTripPlannerSumaty.mp4?alt=media&token=3435d8d8-3be7-4fe9-91e4-c807cdd142df' },
      ];
      return defs.map((def, i) => ({
        title: this.t(def.titleKey),
        videoUrl: def.videoUrl,
        loading: this.tutorialState[i] ? this.tutorialState[i].loading : false,
        error: this.tutorialState[i] ? this.tutorialState[i].error : false,
      }));
    },
    selectedTutorial() {
      if (this.selectedTutorialIndex === null) return null;
      return this.tutorials[this.selectedTutorialIndex];
    }
  },
  methods: {
    toggleTutorial(index) {
      // אם לוחצים על אותה שאלה - סגור אותה
      if (this.selectedTutorialIndex === index) {
        this.closeVideo(index);
        return;
      }
      
      // סגור את הסרטון הקודם אם יש אחד פתוח
      if (this.selectedTutorialIndex !== null) {
        this.closeVideo(this.selectedTutorialIndex);
      }
      
      // פתח את הסרטון החדש
      this.selectedTutorialIndex = index;
      const tutorial = this.tutorials[index];
      if (tutorial && tutorial.videoUrl) {
        this.$set(this.tutorialState[index], 'loading', true);
        this.$set(this.tutorialState[index], 'error', false);

        // המתן ל-nextTick כדי שה-video element ייווצר
        this.$nextTick(() => {
          const videoRef = this.$refs[`video-${index}`];
          if (videoRef && videoRef[0]) {
            const video = videoRef[0];
            video.load();
          }
        });
      }
    },
    closeVideo(index) {
      // עצירת הסרטון אם הוא מנגן
      const videoRef = this.$refs[`video-${index}`];
      if (videoRef && videoRef[0]) {
        const video = videoRef[0];
        video.pause();
        video.currentTime = 0;
      }

      // אם זה הסרטון הפתוח, סגור אותו
      if (this.selectedTutorialIndex === index) {
        this.selectedTutorialIndex = null;
      }
    },
    retryVideo(index) {
      if (this.tutorialState[index]) {
        this.$set(this.tutorialState[index], 'error', false);
        this.$set(this.tutorialState[index], 'loading', true);

        this.$nextTick(() => {
          const videoRef = this.$refs[`video-${index}`];
          if (videoRef && videoRef[0]) {
            const video = videoRef[0];
            video.load();
          }
        });
      }
    },
    onVideoLoadStart(index) {
      if (this.tutorialState[index]) {
        this.$set(this.tutorialState[index], 'loading', true);
      }
    },
    onVideoLoaded(index) {
      if (this.tutorialState[index]) {
        this.$set(this.tutorialState[index], 'loading', false);
      }
    },
    onVideoError(index) {
      if (this.tutorialState[index]) {
        this.$set(this.tutorialState[index], 'error', true);
        this.$set(this.tutorialState[index], 'loading', false);
      }
    },
    scrollToQuestion(index) {
      // גלול לשאלה שנפתחה
      this.$nextTick(() => {
        const questionElement = document.querySelector(`.question-item:nth-child(${index + 1})`);
        if (questionElement) {
          questionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    }
  },
  mounted() {
    // בדוק אם יש פרמטר video ב-URL
    const videoParam = this.$route.query.video;
    if (videoParam !== undefined) {
      const videoIndex = parseInt(videoParam, 10);
      if (!isNaN(videoIndex) && videoIndex >= 0 && videoIndex < this.tutorials.length) {
        // פתח את הסרטון המתאים
        this.toggleTutorial(videoIndex);
        // גלול אליו
        this.scrollToQuestion(videoIndex);
      }
    }
  },
  metaInfo() {
    return {
      title: this.t('tutorialsPageMetaTitle'),
      meta: [
        { property: 'og:title', content: this.t('tutorialsPageMetaTitle') },
        { property: 'og:description', content: this.t('tutorialsPageMetaDescription') },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: 'https://tripplanner.co.il/tutorials' },
        { property: 'og:image', content: 'https://firebasestorage.googleapis.com/v0/b/tripplanner-308b3.appspot.com/o/page_images%2Fphone_display_app.png?alt=media&token=ccfc9989-224e-40be-ae57-aa6ed4db8637' },
        { name: 'description', content: this.t('tutorialsPageMetaDescription') }
      ],
      link: [
        { rel: 'canonical', href: 'https://tripplanner.co.il/tutorials' }
      ],
      script: [
        {
          type: 'application/ld+json',
          json: {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'TripPlanner', item: 'https://tripplanner.co.il' },
              { '@type': 'ListItem', position: 2, name: this.t('tutorials'), item: 'https://tripplanner.co.il/tutorials' }
            ]
          }
        }
      ]
    };
  }
}
</script>

<style scoped>
h1 {
  color: #0d2f29;
  font-weight: 700;
  font-size: 3.4rem;
  margin-bottom: 1.2rem;
  text-align: right;
}

.intro-text {
  font-size: 1.8rem;
  line-height: 2;
  color: #444;
  margin-bottom: 3rem;
  text-align: right;
}

.tutorials-page-content {
  margin-left: 40px;
  text-align: right;
  padding: 2rem 0;
}

.questions-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 800px;
  margin: 0 auto;
}

.question-item {
  width: 100%;
  cursor: pointer;
}

.question-card {
  background-color: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.question-card:hover {
  transform: translateX(-5px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  border-color: #34d0b6;
  background-color: #f8fffe;
}

.question-card.is-open {
  border-color: #34d0b6;
  background-color: #f8fffe;
}

.question-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: #e6f7fa;
  border-radius: 50%;
}

.question-text {
  flex: 1;
  text-align: right;
}

.question-text h3 {
  color: #0d2f29;
  font-weight: 600;
  font-size: 1.8rem;
  margin: 0;
  line-height: 1.4;
}

.question-arrow {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
}

.question-card:hover .question-arrow {
  transform: translateX(-3px);
}

.question-arrow.is-open {
  transform: rotate(90deg);
}

.video-container-wrapper {
  margin-top: 1.5rem;
  padding: 0 2rem 2rem 2rem;
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
  margin-top: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.slide-down-enter-to,
.slide-down-leave {
  max-height: 1000px;
  opacity: 1;
}

.tutorial-video-container {
  position: relative;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  background-color: #000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.tutorial-video {
  width: 100%;
  height: auto;
  display: block;
  max-height: 70vh;
}

.video-loading,
.video-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 2rem;
  color: #666;
  background-color: #f5f5f5;
  border-radius: 12px;
}

.video-loading p,
.video-error p {
  margin-top: 1rem;
  font-size: 1.6rem;
  text-align: center;
}

.retry-button {
  margin-top: 1.5rem;
  padding: 0.8rem 2rem;
  background-color: #34d0b6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.6rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.retry-button:hover {
  background-color: #2ab8a0;
}


.row {
  margin-top: 60px;
}

@media (max-width: 768px) {
  h1 {
    font-size: 2.4rem;
  }

  .question-text h3 {
    font-size: 1.6rem;
  }
  
  .video-container-wrapper {
    padding: 0 1.5rem 1.5rem 1.5rem;
  }

  .intro-text {
    font-size: 1.6rem;
    line-height: 1.6;
  }

  .tutorials-page-content {
    margin-left: 0;
    padding: 1rem;
  }

  .tutorial-card {
    padding: 1.5rem;
  }

  .tutorials-list {
    gap: 2rem;
  }

  .row {
    margin-top: 25px;
  }

  .tutorial-video {
    max-height: 400px;
  }
}

@media (max-width: 480px) {
  .tutorial-card {
    padding: 1rem;
  }

  .question-text h3 {
    font-size: 1.4rem;
  }

  .question-card {
    padding: 1.2rem 1.5rem;
  }
  
  .video-container-wrapper {
    padding: 0 1.2rem 1.2rem 1.2rem;
  }

  .tutorial-video {
    max-height: 50vh;
  }
}
</style>

